/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Table Columns component not supported Sticky
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */
import { useMemo, RefObject, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import debounce from 'lodash/debounce';
import InfiniteScroll from 'react-infinite-scroller';
import { useBlockLayout, useExpanded, useResizeColumns, useTable } from 'react-table';

import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';

import { TABLE_WIDTH_MAX } from '../../../../components/Table/constants';
import { TableLoaderContainerStyled } from '../../../../components/Table/styled/TableStyled';
import { ITableProps } from '../../../../components/Table/Table';
import {
    findNextColumnNotHidden,
    findPrevColumnNotHidden,
    IFixedColumn,
    IFixedColumns,
    isLeftFixed,
    isRightFixed,
} from './helpers';
import './styles.css';

interface IColumnWithFixed {
    className: string;
    headerClassName: string;
    style: {
        flexBasis?: number | string;
        flexGrow: number;
        flexShrink: number;
        width?: number | string;
    };
}

export const getColumnsWithFixed = (
    column: IFixedColumn,
    columns: IFixedColumns,
    index: number,
): IColumnWithFixed => {

    const fixed = column.fixed || false;

    const nextColumn = findNextColumnNotHidden(columns, index);
    const parentIsLastFixedInner = fixed && nextColumn && !nextColumn.fixed;
    const isLastFixed = fixed && !nextColumn || (nextColumn && !nextColumn.fixed);

    const prevColumn = findPrevColumnNotHidden(columns, index);
    const parentIsFirstFixedInner = fixed && prevColumn && !prevColumn.fixed;
    const isFirstFixed = fixed && !prevColumn || (prevColumn && !prevColumn.fixed);

    const className = classNames(column.className, {
        'rthfc-td-fixed': fixed,
        'rthfc-td-fixed-left': isLeftFixed({ fixed }),
        'rthfc-td-fixed-left-last': isLastFixed,
        'rthfc-td-fixed-right': isRightFixed({ fixed }),
        'rthfc-td-fixed-right-first': isFirstFixed,
    });
    const headerClassName = classNames(column.headerClassName, {
        'rthfc-th-fixed': fixed,
        'rthfc-th-fixed-left': isLeftFixed({ fixed }),
        'rthfc-th-fixed-left-last': parentIsLastFixedInner,
        'rthfc-th-fixed-right': isRightFixed({ fixed }),
        'rthfc-th-fixed-right-first': parentIsFirstFixedInner,
    });

    const style = {
        width: column.width,
        flexBasis: column.width,
        flexShrink: 0,
        flexGrow: 1,
    };

    return {
        className, headerClassName, style,
    }
};

let moreLoading = false;

const TableFixedColumnsScroll = ({
                                     columns,
                                     counter,
                                     className,
                                     data,
                                     footerHeight,
                                     onScrollEnd,
                                     innerRef,
                                     isLoading,
                                     isTableFooter,
                                     onResizedChange,
                                     onScrollX,
                                     rowDataName,
                                     style,
                                     tableDataName,
                                 }: ITableProps) => {

    const defaultColumn = useMemo(
        () => ({
            width: 140,
        }),
        [],
    );

    let nextTranslateLeftX = 0;
    let nextTranslateRightX = 0;
    let scrollLeft = 0;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        totalColumnsWidth,
        prepareRow,
    } = useTable(
        // @ts-ignore
        { autoResetExpanded: false, columns, data, defaultColumn },
        useBlockLayout,
        useExpanded,
        useResizeColumns,
    );
    const tableRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const tbodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const theadRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const tfooterRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const [tableHeight, setTableHeight] = useState<string>(style?.height ?? 'auto');

    useEffect(() => {
        const tableRowsContainerHeight = tbodyRef.current?.children[0].clientHeight;
        const theadHeight = theadRef.current?.clientHeight ?? 82;
        const tfooterHeight = isTableFooter ? tfooterRef.current?.clientHeight ?? 45 : 0;
        const availableHeight = window.innerHeight - theadHeight - tfooterHeight - (footerHeight ?? 0);

        if(tableRowsContainerHeight && tableRowsContainerHeight > availableHeight) {
            setTableHeight(style.height);
        } else {
            setTableHeight('auto');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, footerHeight]);

    useEffect(() => {
        moreLoading = false;
        calculatePos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const onScrollHandler = (): void => {
        const tableScrollLeft = tableRef.current?.scrollLeft ?? 0;
        if (scrollLeft !== tableScrollLeft) {
            scrollLeft = tableScrollLeft;
            if (onScrollX) {
                onScrollX(tableScrollLeft);
            }
            calculatePos();
        }
    };

    const calculatePos = (): void => {
        if (tableRef.current) {
            const { scrollLeft, scrollWidth, offsetWidth } = tableRef.current;
            nextTranslateLeftX = scrollLeft;
            nextTranslateRightX = scrollWidth - scrollLeft - offsetWidth;
            updatePos();
        }
    };

    const updatePos = (): void => {
        if (tableRef.current) {
            /* eslint-disable no-param-reassign */
            Array.from(
                tableRef.current.querySelectorAll<HTMLElement>('.rthfc-th-fixed-left, .rthfc-td-fixed-left'))
                .forEach((td) => {
                        td.style.transform = `translate3d(${nextTranslateLeftX}px, 0, 0)`;
                    },
                );

            Array.from(
                tableRef.current.querySelectorAll<HTMLElement>('.rthfc-th-fixed-right, .rthfc-td-fixed-right'))
                .forEach((td) => {
                        td.style.transform = `translate3d(${-nextTranslateRightX}px, 0, 0)`;
                    },
                );
            /* eslint-enable no-param-reassign */
        }
    };

    const renderRows = (row: any, i: number): JSX.Element => {
        prepareRow(row);

        const isFastActionPage = getIsFastActionPage();

        const checked = isFastActionPage ? row.original.fastActionChecked : row.original.checked;

        const rowClassName = classNames('rt-tr', {
            'rt-tr-checked': checked ?? false,
        });
        const rowData = rowDataName ? row.original[rowDataName] ?? '' : '';
        return (
            <div className="rt-tr-group" key={i}>
                <div
                    {...row.getRowProps()}
                    className={rowClassName}
                    data-row-name={rowData}
                >
                    {row.cells.map((cell: any, index: number) => {
                        const {
                            className,
                            style,
                        } = getColumnsWithFixed(cell.column, headerGroups[0].headers, index);

                        const minWidth = cell.column.minResizeWidth ?? 0;
                        const styleWidth = style.width ?? 0;
                        const width = styleWidth > TABLE_WIDTH_MAX
                            ? TABLE_WIDTH_MAX
                            : styleWidth < minWidth
                                ? minWidth
                                : styleWidth;
                        const cellStyle = {
                            ...style,
                            flexBasis: width,
                            maxWidth: width,
                            minWidth,
                            width,
                        };
                        const tdClassName = classNames('rt-td', className);
                        return (
                            <div
                                key={cell.value}
                                {...cell.getCellProps()}
                                className={tdClassName}
                                style={cellStyle}
                            >
                                {cell.render('Cell')}
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    };

    const loadMore = (): void => {
        if (!moreLoading && onScrollEnd && data.length) {
            onScrollEnd();
            moreLoading = true;
        }
    };

    const debounceLoadMore = debounce(loadMore, 100);
    const tableClassName = classNames('rt-table', className);
    const tbodyClass = classNames('rt-tbody', {
        'with-footer': isTableFooter,
    });
    return (
        <div
            ref={innerRef}
            {...getTableProps()}
            className='ReactTable rthfc-fixed-columns rthfc -se'
        >
            <div
                className={tableClassName}
                data-name={tableDataName}
                data-table-name={tableDataName}
                ref={tableRef}
                style={{
                    height: tableHeight,
                    marginBottom: style?.marginBottom,
                }}
                onScroll={onScrollHandler}
            >
                <div className="rt-thead -header" ref={theadRef} style={{ minWidth: totalColumnsWidth }}>
                    {headerGroups.map(headerGroup => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            className="tr rt-tr"
                            key={headerGroup.id}
                        >
                            {headerGroup.headers.map((column: any, index: number) => {
                                const {
                                    headerClassName,
                                    style,
                                } = getColumnsWithFixed(column, headerGroup.headers, index);
                                const minWidth = column.minResizeWidth ?? 0;
                                const styleWidth = style.width ?? 0;
                                const width = styleWidth > TABLE_WIDTH_MAX
                                    ? TABLE_WIDTH_MAX
                                    : styleWidth < minWidth
                                        ? minWidth
                                        : styleWidth;
                                const cellStyle = {
                                    ...style,
                                    flexBasis: width,
                                    maxWidth: width,
                                    minWidth,
                                    width,
                                };
                                let userSelect = 'auto';
                                if (column.isResizing) {
                                    onResizedChange([{ id: column.id, value: width }]);
                                    userSelect = 'none';
                                }
                                const thClassName = classNames('th', 'rt-th', headerClassName);
                                const resizeClassName = classNames('resizer', 'rt-resizer', {
                                    isResizing: column.isResizing,
                                });
                                return (
                                    <div
                                        key={column}
                                        {...column.getHeaderProps()}
                                        className={thClassName}
                                        style={{ ...cellStyle, userSelect }}
                                    >
                                        {column.render('Header')}
                                        {
                                            column.resizable &&
                                            <div
                                                {...column.getResizerProps()}
                                                className={resizeClassName}
                                                data-resizer-name={column.id}
                                            />
                                        }
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                    {
                        isLoading &&
                        <TableLoaderContainerStyled
                            isSticky={false}
                        />
                    }
                </div>

                <div
                    {...getTableBodyProps()}
                    className={tbodyClass}
                    ref={tbodyRef}
                    style={{ minWidth: totalColumnsWidth }}
                >
                    <InfiniteScroll
                        getScrollParent={() => tbodyRef.current}
                        hasMore={rows.length < counter}
                        loadMore={debounceLoadMore}
                        pageStart={0}
                        useWindow={false}
                    >
                        {
                            rows.map(renderRows)
                        }
                    </InfiniteScroll>
                </div>

                {
                    <div
                        className="rt-tfooter -footer"
                        ref={tfooterRef}
                        style={{ minWidth: totalColumnsWidth }}
                    >
                        {isTableFooter && headerGroups.map(headerGroup => (
                            <div
                                {...headerGroup.getHeaderGroupProps()}
                                className="tr rt-tr"
                                key={headerGroup.id}
                            >
                                {headerGroup.headers.map((column: any, index: number) => {
                                    const {
                                        className,
                                        style,
                                    } = getColumnsWithFixed(column, headerGroup.headers, index);
                                    const minWidth = column.minResizeWidth ?? 0;
                                    const styleWidth = style.width ?? 0;
                                    const width = styleWidth > TABLE_WIDTH_MAX
                                        ? TABLE_WIDTH_MAX
                                        : styleWidth < minWidth
                                            ? minWidth
                                            : styleWidth;
                                    const cellStyle = {
                                        ...style,
                                        flexBasis: width,
                                        maxWidth: width,
                                        minWidth,
                                        width,
                                    };
                                    const blockClasses = classNames('td', 'rt-td', className);
                                    return (
                                        <div
                                            key={column}
                                            {...column.getHeaderProps()}
                                            className={blockClasses}
                                            data-col-name={column.id}
                                            data-name={`${column.id}-footer`}
                                            style={{ ...cellStyle }}
                                        >
                                            {column.render('Footer')}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                }
            </div>

        </div>
    )
};

export default TableFixedColumnsScroll;
